import type { Audio } from '@fg/audio';
import { FRUIT_TIERS, HUD } from '../config';
import type { PlaceholderSfxEvent, PlaceholderSfxHandle } from './placeholderSfx';

export interface AudioSystemOptions {
  audio: Audio;
  placeholder: PlaceholderSfxHandle;
  /** Keys that successfully loaded into `@fg/audio`. Used to decide fallback. */
  loaded: Set<string>;
}

export interface AudioSystem {
  playThrow(): void;
  playBounce(): void;
  /** tier is the tier of the RESULTING (merged-into) fruit, 0..8 */
  playMerge(tier: number): void;
  playGameOver(): void;
  playNewBest(): void;
  playButtonTap(): void;
  setMasterVolume(v: number): void;
  setMuted(muted: boolean): void;
  destroy(): void;
}

const BUCKET_KEY: Record<'small' | 'medium' | 'large', string> = {
  small: 'sfx-merge-small',
  medium: 'sfx-merge-medium',
  large: 'sfx-merge-large',
};
const BUCKET_PH: Record<'small' | 'medium' | 'large', PlaceholderSfxEvent> = {
  small: 'merge-small',
  medium: 'merge-medium',
  large: 'merge-large',
};

const EVENT_VOLUMES = {
  'sfx-throw': 0.4,
  'sfx-bounce': 0.3,
  'sfx-merge-small': 0.5,
  'sfx-merge-medium': 0.6,
  'sfx-merge-large': 0.7,
  'sfx-game-over': 0.8,
  'sfx-new-best': 0.9,
  'sfx-button-tap': 0.4,
} as const;

type EventKey = keyof typeof EVENT_VOLUMES;

export function createAudioSystem(opts: AudioSystemOptions): AudioSystem {
  const bounceTimes: number[] = [];

  function playOrFallback(key: EventKey, ph: PlaceholderSfxEvent) {
    if (!opts.loaded.has(key)) {
      opts.placeholder.play(ph);
      return;
    }
    const id = opts.audio.play(key, { volume: EVENT_VOLUMES[key] });
    if (id === -1) opts.placeholder.play(ph);
  }

  return {
    playThrow() {
      playOrFallback('sfx-throw', 'throw');
    },
    playBounce() {
      const now = Date.now();
      while (bounceTimes.length > 0 && now - bounceTimes[0] > 1000) bounceTimes.shift();
      if (bounceTimes.length >= HUD.bounceSfxMaxPerSec) return;
      bounceTimes.push(now);
      playOrFallback('sfx-bounce', 'bounce');
    },
    playMerge(tier) {
      const t = FRUIT_TIERS[tier];
      if (!t) return;
      const bucket = t.sfxBucket;
      playOrFallback(BUCKET_KEY[bucket] as EventKey, BUCKET_PH[bucket]);
    },
    playGameOver() {
      playOrFallback('sfx-game-over', 'game-over');
    },
    playNewBest() {
      playOrFallback('sfx-new-best', 'new-best');
    },
    playButtonTap() {
      playOrFallback('sfx-button-tap', 'button-tap');
    },
    setMasterVolume(v) {
      opts.audio.setMasterVolume(v);
    },
    setMuted(muted) {
      opts.audio.setMuted(muted);
    },
    destroy() {
      opts.placeholder.destroy();
      opts.audio.destroy();
    },
  };
}
